import { useEffect, useState } from 'react';
import { printInOwnWindow } from '../utils/printWindow';
import { greetingFor, looksLikeCompany } from '../utils/ownerGreeting';
import { Card, CardHeader, CardTitle, CardContent, Button, Input } from './ui';
import { api } from '../lib/api';
import { NOT_CHECKED_MARK } from '../lib/fieldValues';
import type { ProposalGenerateInput } from '../types';

/**
 * The listing proposal generator - what Apex calls an Executive Summary - reachable from
 * wherever the work happens.
 *
 * ONE component, three callers (the deal page, the property database and the analyzer property
 * page), for the reason the LOI dialog says: three hand-kept copies of one document's form is
 * the drift this repo keeps paying for. What differs is only what the caller can PREFILL and
 * whether there is a deal to file against.
 *
 * It replaces `listingProposalPdf.ts`, a 746-line client-side jsPDF builder with the layout
 * invented rather than read off his letters, wired to a single page. Same rebuild the LOI got.
 */

export interface ProposalPrefill {
  /** Suggested, always editable. Never a price - that is the recommendation. */
  addressLine?: string | null;
  city?: string | null;
  zoning?: string | null;
  ownerName?: string | null;
  propertyType?: string | null;
  propertyClass?: string | null;
  /**
   * ⭐⭐ SLICE 1 (the 98th) - the facts that were typed on EVERY proposal.
   *
   * `proposalBuilderService` REFUSES without the owner's address, the subject and the scope
   * description, and none of them could be prefilled at all - so a chat door that could not
   * carry them would open a dialog he still had to fill by hand, which is the friction this
   * exists to remove.
   * ⚠️ `salutationName` is an OVERRIDE rather than a gap: the form already derives one from the
   *   last word of the owner's name (Abbas Satrap -> Satrap). "Mr. & Mrs. Kardani" and a trust
   *   both break that guess, and only he knows.
   */
  salutationName?: string | null;
  ownerAddressLines?: string[] | null;
  subject?: string | null;
  scopeDescription?: string | null;
  /**
   * ⭐⭐ A price HE STATED, into the first tier. Added in the 98th after a live run dropped one:
   * the tool emitted `askingPrice`, `PageActionFields` carried it, and nothing here declared it,
   * so the dialog opened with the most important field on the letter blank right after he had
   * said it out loud.
   *
   * ⛔ NOT a contradiction of the comment above. "Never a price" forbids the APP suggesting a
   *   figure from a record, a valuation or a comp - that is his recommendation to make. This is
   *   his own number, carried, exactly as the LOI carries a purchase price he states.
   */
  askingPrice?: number | null;
  askingPriceHigh?: number | null;
  /** DOC-READ (the 157th): the APN and the year built - on every letter of his, and filled from nothing until now. */
  apn?: string | null;
  yearBuilt?: string | null;
}

/** One document that filled boxes, and which ones (backend `ProposalPrefillSource`). */
interface PrefillSource {
  title: string;
  filedAt: string | null;
  documentType: string | null;
  fields: string[];
  namesReordered: boolean;
}

/** What `GET /proposals/prefill/:id` answers: the record's facts, the documents' where the record is blank. */
type LoadedPrefill = ProposalPrefill & {
  fromDocuments?: PrefillSource[];
  recordOwnerName?: string | null;
  /** DOCTRACK part 3 (rule 9): the boxes the record filled from a title report nobody has checked. */
  notChecked?: Array<'ownerName' | 'propertyType' | 'yearBuilt'>;
};

/** A prefill with its blanks dropped, so a null in one layer never blanks a value from another. */
const filled = (p: ProposalPrefill | null | undefined): ProposalPrefill =>
  Object.fromEntries(Object.entries(p ?? {}).filter(([, v]) => v !== null && v !== undefined && v !== '')) as ProposalPrefill;

/** One value however it is spelled - case, punctuation and "&" for "and" aside (the chat may pass a document's own owner back). */
const sameValue = (a: unknown, b: unknown): boolean => {
  const key = (v: unknown): string =>
    (Array.isArray(v) ? v.join(' ') : String(v ?? '')).toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '');
  return key(a) === key(b);
};

/** The box each word in a document's note stands for (the words are backend `proposalPrefillService`'s credits). */
const FIELD_BOX: Record<string, keyof ProposalPrefill> = {
  'the owner': 'ownerName',
  "the owner's mailing address": 'ownerAddressLines',
  'the property type': 'propertyType',
  'the building size': 'propertyType',
  'the lot size': 'propertyType',
  'the year built': 'yearBuilt',
  'the APN': 'apn',
  'the zoning': 'zoning',
};

const KIND_WORDS: Record<string, string> = {
  title_report: 'the title report', property_profile: 'the property profile', offering_memorandum: 'the OM',
};
const kindOf = (documentType: string | null): string => KIND_WORDS[documentType ?? ''] ?? 'the document';
const capFirst = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
const filedDay = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', year: 'numeric' }) : '';
/** "the owner and the owner's mailing address" / "a, b and c" */
const andList = (xs: string[]): string => (xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

interface GeneratedProposal {
  proposal_text: string;
  proposal_html: string;
  docx_base64: string;
  filename: string;
  filed: boolean;
  class_match: { key: string; label: string; exact: boolean; requested: string };
}

/**
 * The classes with a letter of Joseph's behind them. Everything else takes the RETAIL wording -
 * his call - and the result panel says so rather than hiding it in a console.
 */
const CLASSES = [
  { value: 'retail', label: 'Retail (also automotive, restaurant, strip centre)' },
  { value: 'industrial', label: 'Industrial / warehouse / distribution' },
  { value: 'industrial_condo', label: 'Industrial condo / small bay' },
  { value: 'land', label: 'Land' },
  { value: 'office', label: 'Office (no example yet - uses retail wording)' },
];

/** Letter dates are business dates - America/Los_Angeles, never UTC. */
const laLongDate = (d: Date): string =>
  d.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: 'long', day: 'numeric' });

const lines = (s: string): string[] => s.split('\n').map((l) => l.trim()).filter(Boolean);

export default function ProposalGeneratorDialog({
  open,
  onClose,
  prefill,
  recordId,
  fallback,
  dealId,
  onFiled,
}: {
  open: boolean;
  onClose: () => void;
  /** What HE stated (the chat's fields) - on top of everything the record and its documents fill. */
  prefill?: ProposalPrefill;
  /**
   * ⭐ DOC-READ (the 157th): the property behind the form - a Property Database id or a My Deals id. Each open reads its
   *   prefill (`GET /proposals/prefill/:id`): the record's facts and, where the record is blank, the title report or
   *   property profile filed on it - the owners, their mailing address, the type and size, the APN, the year.
   */
  recordId?: string | null;
  /** The record's identity, used only when that read fails - a failed read still opens a usable form. */
  fallback?: ProposalPrefill;
  /** When present the proposal is filed against this deal. */
  dealId?: string | null;
  /** Called after a proposal is filed, so the caller can refresh its document list. */
  onFiled?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GeneratedProposal | null>(null);
  const [showMarketing, setShowMarketing] = useState(false);

  /**
   * ⭐ THE FORM WAITS FOR ITS PREFILL, and is drawn once, keyed on the read's outcome. Its boxes are uncontrolled
   *   (`defaultValue`): an UNTOUCHED box does follow a value that arrives later (measured - React updates the default,
   *   and a box nobody typed in shows it), but a box he has already typed in does not, so a form drawn while the read
   *   is out could end up half his and half the document's. Waiting a moment avoids that mix.
   */
  const [load, setLoad] = useState<{
    state: 'idle' | 'loading' | 'ready' | 'failed'; data: LoadedPrefill | null; recordId: string | null;
  }>({ state: 'idle', data: null, recordId: null });
  useEffect(() => {
    if (!open || !recordId) {
      setLoad({ state: 'idle', data: null, recordId: null });
      return;
    }
    let live = true;
    setLoad({ state: 'loading', data: null, recordId });
    // ⚠️ Through a promise, so even a missing client method becomes a FAILED read (the fallback form), never a crash.
    Promise.resolve()
      .then(() => api.getProposalPrefill(recordId))
      .then((res) => { if (live) setLoad({ state: 'ready', data: (res?.prefill ?? null) as LoadedPrefill | null, recordId }); })
      .catch(() => { if (live) setLoad({ state: 'failed', data: null, recordId }); });
    return () => { live = false; };
  }, [open, recordId]);
  /**
   * The review's minor 6: the read starts in an effect, AFTER the first frame - so on each open, and on a move to another
   *   record, there is a frame where the state still describes no read, or the LAST record's. Both count as waiting: no
   *   frame of the form is drawn from the wrong values.
   */
  const settled = load.recordId === recordId ? load.state : 'idle';
  const waiting = !!recordId && (settled === 'idle' || settled === 'loading');
  const ready = !!recordId && settled === 'ready';

  // The record's facts (or, if the read failed, its identity), then his own words on top.
  const loaded = ready ? filled(load.data) : filled(fallback);
  const chat = filled(prefill);
  /**
   * ⭐ THE OWNER BLOCK IS ONE UNIT (the review's I2). An owner the chat names that is not the one the record or a document
   *   gives must not inherit THEIR greeting and mailing address - a letter to John Doe that opens "Dear Mr. Siddiqi," and
   *   is posted to the Siddiqis. What the chat names of the block is used; the rest of it is left blank for him.
   */
  const ownerFromChat = chat.ownerName !== undefined && !sameValue(chat.ownerName, loaded.ownerName);
  const base: ProposalPrefill = { ...loaded };
  if (ownerFromChat) {
    delete base.salutationName;
    delete base.ownerAddressLines;
  }
  const merged: ProposalPrefill = { ...base, ...chat };
  /** A box still holding what the record or document put there - the only kind a note may credit to a document. */
  const stillLoaded = (key: keyof ProposalPrefill): boolean => base[key] !== undefined && sameValue(merged[key], base[key]);
  const sources = (ready ? load.data?.fromDocuments ?? [] : [])
    .map((s) => ({
      ...s,
      fields: s.fields.filter((f) => (FIELD_BOX[f] ? stillLoaded(FIELD_BOX[f]) : true)),
      namesReordered: s.namesReordered && stillLoaded('ownerName'),
    }))
    .filter((s) => s.fields.length > 0);
  // The document whose owner the chat replaced - named, so an empty address box is not read as the read failing again.
  const replacedOwnerSource = ownerFromChat
    ? (ready ? load.data?.fromDocuments ?? [] : []).find((s) => s.fields.includes('the owner')) ?? null
    : null;
  const recordOwner = ready && !ownerFromChat ? load.data?.recordOwnerName ?? null : null;
  /** DOCTRACK part 3 (rule 9): under a box the record filled from a not-checked title report, while it still holds it. */
  const notCheckedMark = (key: 'ownerName' | 'propertyType' | 'yearBuilt'): string | undefined =>
    (ready && (load.data?.notChecked ?? []).includes(key) && stillLoaded(key) ? NOT_CHECKED_MARK : undefined);

  // AIW1-F4: what the owner and salutation boxes hold now, for the note beside the salutation box.
  const [ownerNow, setOwnerNow] = useState(merged.ownerName ?? '');
  const [salutationNow, setSalutationNow] = useState(merged.salutationName ?? '');
  // The pages keep this dialog mounted while closed: each OPEN starts from the boxes' prefill again. ⚠️ Keyed on the two
  //   VALUES, never the prefill object - the record page passes a new inline object on every render (the review).
  const prefillOwner = merged.ownerName ?? '';
  const prefillSalutation = merged.salutationName ?? '';
  // DOCTRACK part 3 (the review, M1): the type and year boxes as typed, so a box he typed over loses its not-checked mark.
  const [typeNow, setTypeNow] = useState(merged.propertyType ?? '');
  const [yearNow, setYearNow] = useState(merged.yearBuilt ?? '');
  const prefillType = merged.propertyType ?? '';
  const prefillYear = merged.yearBuilt ?? '';
  useEffect(() => {
    if (!open) return;
    setOwnerNow(prefillOwner);
    setSalutationNow(prefillSalutation);
    setTypeNow(prefillType);
    setYearNow(prefillYear);
  }, [open, prefillOwner, prefillSalutation, prefillType, prefillYear]);

  /**
   * Lock the page behind the dialog - BOTH html and body. Body alone relies on CSS overflow
   * PROPAGATION to the viewport, which holds only while html's own overflow stays visible.
   * The LOI dialog learned this when the property map kept scrolling behind a modal.
   */
  useEffect(() => {
    if (!open) return;
    const body = document.body;
    const html = document.documentElement;
    const prev = { bodyOverflow: body.style.overflow, bodyPad: body.style.paddingRight, htmlOverflow: html.style.overflow };
    const gap = window.innerWidth - html.clientWidth;
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    if (gap > 0) body.style.paddingRight = `${gap}px`;
    return () => {
      body.style.overflow = prev.bodyOverflow;
      body.style.paddingRight = prev.bodyPad;
      html.style.overflow = prev.htmlOverflow;
    };
  }, [open]);

  if (!open) return null;

  /**
   * ⛔ THE PRICE METRICS ARE COMPUTED ONLY WHEN HE ASKS. His own Satrap letter prints $5.10/SF
   * where the arithmetic gives $5.09, and $7.10 where it gives $7.11 - he rounds to a
   * marketing number. Deriving silently would overwrite a figure he chose, so this is a button
   * he presses, and the result stays editable.
   */
  const computeMetrics = (): void => {
    const form = document.getElementById('proposal-form') as HTMLFormElement | null;
    if (!form) return;
    const acres = parseFloat((form.elements.namedItem('land_acres') as HTMLInputElement)?.value ?? '');
    if (!Number.isFinite(acres) || acres <= 0) {
      setError('Enter the land area in acres first - that is what the $/SF and $/AC are computed from.');
      return;
    }
    setError(null);
    const sf = acres * 43560;
    for (const n of ['1', '2', '3']) {
      const price = parseFloat((form.elements.namedItem(`price${n}`) as HTMLInputElement)?.value ?? '');
      if (!Number.isFinite(price) || price <= 0) continue;
      const perSf = form.elements.namedItem(`per_sf${n}`) as HTMLInputElement | null;
      const perAc = form.elements.namedItem(`per_ac${n}`) as HTMLInputElement | null;
      if (perSf) perSf.value = `$${(price / sf).toFixed(2)}/SF`;
      if (perAc) perAc.value = `$${Math.round(price / acres / 1000)}K/AC`;
    }
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);

    const fd = new FormData(e.currentTarget);
    const g = (n: string): string => ((fd.get(n) as string) ?? '').trim();
    const num = (n: string): number => {
      const v = parseFloat(g(n));
      return Number.isFinite(v) ? v : 0;
    };

    const tiers = ['1', '2', '3']
      .map((n) => ({
        label: g(`tier_label${n}`) || null,
        price: num(`price${n}`),
        priceHigh: num(`price_high${n}`) || null,
        perSf: g(`per_sf${n}`) || null,
        perAcre: g(`per_ac${n}`) || null,
        note: g(`tier_note${n}`) || null,
      }))
      /**
       * 🚨 THE PRICE IS WHAT MAKES A TIER, NOT THE LABEL (fixed in the 98th).
       *
       * This read `t.label && (...)` first, so a tier carrying a PRICE and no LABEL was dropped
       * before the price filter ever saw it - and the label's own hint invites a blank one:
       * *"Tier label (blank = your wording for this class)"*. Typing a price and leaving the
       * label alone, which is the normal case, produced NO tiers at all and the builder then
       * refused with *"at least one recommended asking price"* - telling him he gave no price on
       * a form where he had just typed one.
       *
       * ⚠️ The old condition's second half was dead too: `g('price' + ['1','2','3'][0])` is
       *   always `price1`, whichever row was being tested.
       * ⛔ A row he never touched is simply not a tier, and a label with no price is an
       *   unfinished row - neither becomes an empty tier. The builder still refuses when NO row
       *   carries a price, which is the gap actually worth naming.
       */
      .filter((t) => t.price > 0);

    const proposal: ProposalGenerateInput = {
      letterDate: laLongDate(new Date()),
      owner: {
        // AIW1-F4: a person's name when he gives one, the business name when that is all we have.
        ...greetingFor(g('owner_name'), g('honorific'), g('salutation_name')),
        name: g('owner_name'),
        entityName: g('entity_name') || null,
        addressLines: lines(g('owner_address')),
      },
      property: {
        addressLine: g('address_line'),
        city: g('city'),
        propertyType: g('property_type'),
        zoning: g('zoning'),
        apn: g('apn') || null,
        yearBuilt: g('year_built') || null,
        currentUse: g('current_use') || null,
      },
      propertyClass: g('property_class') || null,
      subject: g('subject'),
      scopeDescription: g('scope_description'),
      representedParty: g('represented_party') || null,
      priceTiers: tiers,
      keyConsiderations: lines(g('key_considerations')),
      considerationsNarrative: g('considerations_narrative') || null,
      challenges: lines(g('challenges')),
      closingNote: g('closing_note') || null,
      marketing: {
        photographyFocus: g('mk_photography') || null,
        sitePlanUses: g('mk_site_plan') || null,
        buyerTypes: g('mk_buyers') || null,
        brokerSpecialties: g('mk_brokers') || null,
        edFocus: g('mk_ed') || null,
      },
      // Both sign by default; the box takes Joseph OFF for a Robert-only letter.
      includeJoseph: fd.get('robert_only') !== 'on',
      omitKeyConsiderations: fd.get('omit_considerations') === 'on',
      omitChallenges: fd.get('omit_challenges') === 'on',
      omitClosingNote: fd.get('omit_note') === 'on',
    };

    try {
      const res = await api.generateProposal(proposal, dealId ?? undefined);
      setResult(res.proposal);
      if (res.proposal.filed) onFiled?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate the listing proposal');
    } finally {
      setBusy(false);
    }
  };

  const downloadDocx = () => {
    if (!result?.docx_base64) return;
    const bytes = Uint8Array.from(atob(result.docx_base64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.filename || 'Listing Proposal.docx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // MOB1 (the 126th): the print window carries its way back - on his phone it had none.
  const print = () => {
    if (result) printInOwnWindow(result.proposal_html);
  };

  const close = () => {
    setResult(null);
    setError(null);
    onClose();
  };

  const ta = 'w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm';

  return (
    /* z-[1200], NOT z-50: Leaflet gives its own panes z-index 400-650, and at z-50 the property
       map draws straight THROUGH a fixed overlay. */
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1200] p-4">
      <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{result ? 'Listing Proposal' : 'Generate Listing Proposal'}</CardTitle>
            <Button variant="ghost" size="sm" onClick={close}>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          {result ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Button onClick={downloadDocx}>Download Word (.docx)</Button>
                <Button variant="outline" onClick={print}>Print / PDF</Button>
                <Button variant="ghost" onClick={close}>Done</Button>
              </div>
              <p className="text-xs text-gray-500">
                {result.filed
                  ? 'Filed on this deal - it is in the Deal Room documents list.'
                  : 'Not filed anywhere - download the Word file to keep it.'}
              </p>
              {result.class_match && (
                <p className={`text-xs ${result.class_match.exact ? 'text-gray-500' : 'text-warning'}`}>
                  {result.class_match.exact
                    ? `Wording: your ${result.class_match.label.toLowerCase()} proposal.`
                    : `No ${result.class_match.requested || 'class'} example on file - used your RETAIL wording. Check the key considerations and the closing note before sending.`}
                </p>
              )}
              <div className="bg-gray-50 rounded-lg p-4 max-h-[45vh] overflow-y-auto">
                <pre className="whitespace-pre-wrap text-xs text-gray-700 font-sans">{result.proposal_text}</pre>
              </div>
            </div>
          ) : waiting ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              Filling in from the property record and the documents filed on it...
            </p>
          ) : (
            <form id="proposal-form" key={`${recordId ?? 'none'}:${settled}`} onSubmit={submit} className="space-y-4">
              <p className="text-xs text-gray-500 -mt-1">
                Built from your own proposals - Satrap (land), 1201 E 6th and 7664 Indiana
                (retail), 9216 Center (industrial condo), 2026 W Valley (industrial). The standing
                language is yours; you supply the facts about the property and the price.
                {dealId ? ' The finished proposal is filed on this deal.' : ''}
              </p>

              {/* DOC-READ (the 157th): what a filed document put in the boxes, named, so he checks it before sending. */}
              {/* ⚠️ Keyed by position too: two documents can share a title (the review's minor 12). */}
              {sources.map((s, i) => (
                <p key={`${i}:${s.title}`} className="text-xs p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-900">
                  Filled in from {kindOf(s.documentType)} filed on this property -
                  {' '}{s.title}{s.filedAt ? ` (${filedDay(s.filedAt)})` : ''}: {andList(s.fields)}.
                  {s.namesReordered ? ' It lists the owners last name first; they are put in letter order here.' : ''}
                  {' '}Check them before you send it.
                </p>
              ))}
              {replacedOwnerSource && (
                <p className="text-xs p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900">
                  {capFirst(kindOf(replacedOwnerSource.documentType))} filed on this property names {loaded.ownerName} as the
                  owner, not {merged.ownerName}
                  {replacedOwnerSource.fields.includes("the owner's mailing address") ? ' - so the mailing address it gives is not used here' : ''}.
                </p>
              )}
              {settled === 'failed' && (
                <p className="text-xs p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900">
                  Could not read this property's record and documents just now - fill these in, or close and open it again.
                </p>
              )}

              <div className="border-b pb-4">
                <h4 className="font-medium text-gray-900 mb-3">Owner</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Honorific</label>
                    <select name="honorific" defaultValue="Mr." className={ta}>
                      <option>Mr.</option>
                      <option>Ms.</option>
                      <option>Mrs.</option>
                      <option>Mr. &amp; Mrs.</option>
                    </select>
                  </div>
                  <Input
                    name="owner_name" required label="Owner name *" defaultValue={merged.ownerName ?? ''} placeholder="Abbas Satrap"
                    onChange={(e) => setOwnerNow(e.target.value)}
                    hint={recordOwner ? `The property record names ${recordOwner}.` : (sameValue(ownerNow, merged.ownerName) ? notCheckedMark('ownerName') : undefined)}
                  />
                  <Input
                    name="salutation_name" label="Salutation name" placeholder="Satrap" defaultValue={merged.salutationName ?? ""}
                    onChange={(e) => setSalutationNow(e.target.value)}
                    hint={looksLikeCompany(ownerNow) && !salutationNow.trim() ? `Greets the company by name ("Dear ${ownerNow.trim()},"). Type a person's name to greet them instead.` : undefined}
                  />
                  <div className="col-span-3">
                    <Input name="entity_name" label="Entity line (optional)" placeholder="MA Riverside, LLC" />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Owner address *</label>
                  <textarea name="owner_address" required rows={2} className={ta} placeholder={'3908 CASTLEROCK RD\nMALIBU, CA 90265-5626'} defaultValue={(merged.ownerAddressLines ?? []).join("\n")} />
                </div>
              </div>

              <div className="border-b pb-4">
                <h4 className="font-medium text-gray-900 mb-3">The property</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Property class *
                    </label>
                    {/* 🚨 The 125th: the class the chat passed was carried nowhere - hard-set to retail. */}
                    <select name="property_class" defaultValue={merged.propertyClass ?? 'retail'} className={ta}>
                      {CLASSES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    <p className="text-[11px] text-gray-400 mt-1">
                      Sets the key considerations, the closing note, the tier labels and the
                      Blueprint targeting - from your own letter for that class.
                    </p>
                  </div>
                  <div className="col-span-2">
                    <Input name="address_line" required label="Address line *" defaultValue={merged.addressLine ?? ''} placeholder="1120 E. 1st Street & Pennsylvania Ave" />
                  </div>
                  <Input name="city" required label="City *" defaultValue={merged.city ?? ''} placeholder="Beaumont" />
                  <Input name="property_type" required label="Property type and size *" defaultValue={merged.propertyType ?? ''} placeholder="Vacant Land (+/- 4.51 Acres)"
                    onChange={(e) => setTypeNow(e.target.value)} hint={sameValue(typeNow, merged.propertyType) ? notCheckedMark('propertyType') : undefined} />
                  <Input name="apn" label="APN" defaultValue={merged.apn ?? ''} placeholder="418-123-017" />
                  <Input name="year_built" label="Year built" defaultValue={merged.yearBuilt ?? ''} placeholder="1971"
                    onChange={(e) => setYearNow(e.target.value)} hint={sameValue(yearNow, merged.yearBuilt) ? notCheckedMark('yearBuilt') : undefined} />
                  <div className="col-span-2">
                    <Input name="current_use" label="Current use" placeholder="Two in-place tenants (laundromat + market)" />
                  </div>
                  <div className="col-span-2">
                    <Input name="zoning" required label="Zoning *" defaultValue={merged.zoning ?? ''} placeholder="Community Commercial with Transit Oriented District (TOD) Overlay" />
                  </div>
                  <div className="col-span-2">
                    <Input name="subject" required label="What the opening says is being marketed *" placeholder="two parcels totaling 4.50 acres on 1st Street Beaumont, CA" defaultValue={merged.subject ?? ""} />
                  </div>
                  <div className="col-span-2">
                    <Input name="scope_description" required label="What Scope of Service says is being sold *" placeholder="4.5 acres on 1st Street Beaumont, CA" defaultValue={merged.scopeDescription ?? ""} />
                  </div>
                  <div className="col-span-2">
                    <Input name="represented_party" label="Represented party (defaults to the owner)" />
                  </div>
                </div>
              </div>

              <div className="border-b pb-4">
                <div className="flex items-end justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Recommended asking</h4>
                  <div className="flex items-end gap-2">
                    <Input name="land_acres" label="Land acres" className="w-28" placeholder="4.51" />
                    <Button type="button" variant="outline" size="sm" onClick={computeMetrics}>
                      Compute $/SF and $/AC
                    </Button>
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 mb-3">
                  Nothing is priced for you. The button fills the metrics from the price and the
                  acreage; they stay editable, because your own letters round them.
                </p>
                {['1', '2', '3'].map((n) => (
                  <div key={n} className="grid grid-cols-12 gap-2 mb-2">
                    <div className="col-span-4">
                      <Input
                        name={`tier_label${n}`}
                        label={n === '1' ? 'Tier label (blank = your wording for this class)' : ''}
                      />
                    </div>
                    <div className="col-span-2">
                      {/* AIW1-F4: "Price *" is REQUIRED - refused before Generate, not in red after it. */}
                      <Input name={`price${n}`} required={n === '1'} label={n === '1' ? 'Price *' : ''} placeholder="700000" defaultValue={n === "1" ? (prefill?.askingPrice ?? "") : ""} />
                    </div>
                    <div className="col-span-2">
                      <Input name={`price_high${n}`} label={n === '1' ? 'to (range)' : ''} placeholder="750000" defaultValue={n === "1" ? (prefill?.askingPriceHigh ?? "") : ""} />
                    </div>
                    <div className="col-span-2">
                      <Input name={`per_sf${n}`} label={n === '1' ? '$/SF' : ''} />
                    </div>
                    <div className="col-span-2">
                      <Input name={`per_ac${n}`} label={n === '1' ? '$/AC' : ''} />
                    </div>
                    <div className="col-span-12">
                      <Input name={`tier_note${n}`} placeholder={n === '2' ? 'NOTE under this tier (optional)' : ''} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-b pb-4">
                <h4 className="font-medium text-gray-900 mb-1">Key considerations</h4>
                <p className="text-[11px] text-gray-400 mb-2">
                  Leave both blank and the whole section is omitted - your 370 S I Street proposal
                  has none. Nothing is invented about the property.
                </p>
                <label className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                  <input type="checkbox" name="omit_considerations" className="rounded border-gray-300" />
                  Leave this section out entirely
                </label>
                <textarea name="key_considerations" rows={3} className={ta} placeholder={'One per line. Blank uses your standing list for this class.'} />
                <textarea name="considerations_narrative" rows={3} className={`${ta} mt-2`} placeholder="The highest-and-best-use paragraph, in your words." />
              </div>

              <div className="border-b pb-4">
                <h4 className="font-medium text-gray-900 mb-1">Challenges and closing note</h4>
                <p className="text-[11px] text-gray-400 mb-2">Both omitted entirely when blank.</p>
                <div className="flex items-center gap-4 mb-2">
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" name="omit_challenges" className="rounded border-gray-300" />
                    No challenges section
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" name="omit_note" className="rounded border-gray-300" />
                    No closing note
                  </label>
                </div>
                <textarea name="challenges" rows={2} className={ta} placeholder={'One per line\nThe property is within a known fault line'} />
                <textarea name="closing_note" rows={3} className={`${ta} mt-2`} placeholder="The closing NOTE paragraph." />
              </div>

              <div className="border-b pb-4">
                <button
                  type="button"
                  onClick={() => setShowMarketing((v) => !v)}
                  className="text-sm font-medium text-gray-900 flex items-center gap-1"
                >
                  {showMarketing ? '▾' : '▸'} Tailor the Marketing Blueprint (optional)
                </button>
                <p className="text-[11px] text-gray-400 mt-1">
                  All six phases are delivered either way. These five points are the only ones that
                  would otherwise say something about this specific property, so they default to
                  wording that claims nothing.
                </p>
                {showMarketing && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <Input name="mk_photography" label="Photography focus" placeholder="corner exposure, freeway proximity, surrounding retail/industrial" />
                    <Input name="mk_site_plan" label="Conceptual layouts" placeholder="retail, gas/QSR pad, multifamily" />
                    <Input name="mk_buyers" label="Canvassing targets" placeholder="QSR, gas station, multifamily developers" />
                    <Input name="mk_brokers" label="Broker specialties" placeholder="land/retail/multifamily" />
                    <div className="col-span-2">
                      <Input name="mk_ed" label="Economic Development angle" placeholder="to highlight TOD incentives" />
                    </div>
                  </div>
                )}
              </div>

              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input type="checkbox" name="robert_only" className="rounded border-gray-300 mt-0.5" />
                <span>
                  Robert signs alone (leave Joseph off this one)
                  <span className="block text-[11px] text-gray-400">
                    By default both blocks appear side by side and the page footer carries both
                    sets of contact details. Both signatures are applied automatically.
                  </span>
                </span>
              </label>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={close}>Cancel</Button>
                <Button type="submit" disabled={busy}>
                  {busy ? 'Generating...' : 'Generate proposal'}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
